import React, { useState } from 'react';
import {
  SpeedDial,
  SpeedDialIcon,
  SpeedDialAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Snackbar,
  Alert
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import BugReportIcon from '@mui/icons-material/BugReport';
import ChatIcon from '@mui/icons-material/Chat';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { links } from '../config/links';

const FeedbackWidget = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const feedbackOptions = [
    {
      icon: <WhatsAppIcon />,
      name: 'WhatsApp Group',
      color: '#25D366',
      action: () => {
        window.open(links.external.whatsappGroup, '_blank');
        handleClose();
      }
    },
    {
      icon: <BugReportIcon />,
      name: 'Report Bug',
      color: '#f44336',
      action: () => {
        setFeedbackType('bug');
        setDialogOpen(true);
        handleClose();
      }
    },
    {
      icon: <RateReviewIcon />,
      name: 'Give Feedback',
      color: '#2196f3',
      action: () => {
        setFeedbackType('feedback');
        setDialogOpen(true);
        handleClose();
      }
    },
    {
      icon: <EmailIcon />,
      name: 'Email Support',
      color: '#ff9800',
      action: () => {
        window.location.href = links.emailTemplates.feedback.getMailtoLink(user?.username);
        handleClose();
      }
    }
  ];

  const handleSubmitFeedback = async () => {
    try {
      // Here you would typically send the feedback to your API
      // For now, we'll just show a success message
      await api.post('/feedback', {
        type: feedbackType,
        message: feedbackText,
        user_id: user?.id,
        user_email: user?.username
      });
      
      setSnackbar({
        open: true,
        message: 'Thank you for your feedback! We\'ll review it shortly.',
        severity: 'success'
      });
      setDialogOpen(false);
      setFeedbackText('');
    } catch (error) {
      // If API fails, fallback to email
      const subject = encodeURIComponent(`${feedbackType === 'bug' ? 'Bug Report' : 'Feedback'} from ${user?.username || 'User'}`);
      const body = encodeURIComponent(feedbackText);
      const feedbackSubject = `${feedbackType === 'bug' ? 'Bug Report' : 'Feedback'} from ${user?.username || 'User'}`;
      window.location.href = links.emailTemplates.feedback.getMailtoLink(user?.username, feedbackSubject, feedbackText);
      
      setSnackbar({
        open: true,
        message: 'Opening email client to send feedback...',
        severity: 'info'
      });
      setDialogOpen(false);
      setFeedbackText('');
    }
  };

  return (
    <>
      <SpeedDial
        ariaLabel="Feedback options"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        icon={<SpeedDialIcon icon={<ChatIcon />} openIcon={<CloseIcon />} />}
        onClose={handleClose}
        onOpen={handleOpen}
        open={open}
      >
        {feedbackOptions.map((option) => (
          <SpeedDialAction
            key={option.name}
            icon={option.icon}
            tooltipTitle={option.name}
            onClick={option.action}
            sx={{
              '& .MuiSpeedDialAction-fab': {
                backgroundColor: option.color,
                color: 'white',
                '&:hover': {
                  backgroundColor: option.color,
                  filter: 'brightness(0.9)'
                }
              }
            }}
          />
        ))}
      </SpeedDial>

      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {feedbackType === 'bug' ? 'Report a Bug' : 'Share Your Feedback'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {feedbackType === 'bug' 
                ? 'Please describe the issue you encountered. Include steps to reproduce if possible.'
                : 'We value your feedback! Let us know how we can improve your experience.'}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              placeholder={feedbackType === 'bug' 
                ? 'Describe the bug...\n\nSteps to reproduce:\n1. \n2. \n3. '
                : 'Your feedback here...'}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmitFeedback} 
            variant="contained"
            disabled={!feedbackText.trim()}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackWidget;